import React, { CSSProperties, MouseEvent, FC, useEffect } from 'react';
import Icon from "../Icon/Icon";
import { IconColorE, IconSizeE } from "../../models/enums";
import { Span } from "../Text/Text";
import { clss } from "../../utils/helpers/styleHelpers";
import { useAppSelector } from '../../store/hooks';
import { selectDirection, selectIsLoading } from '../../store/selectors/appSelectors';
import { useNavigate } from 'react-router-dom';

import styles from './button.module.scss';

export enum ButtonModeE {
    FILLED = 'filled',
    OUTLINED = "outlined",
    LINK = 'link',
};

// spec 48 — which entity the click acts on. CAMPAIGN swaps the blue for $campaign; DELIVERY is today's look.
export enum ButtonIntentE {
    DELIVERY = 'delivery',
    CAMPAIGN = 'campaign',
};

type ButtonTypeT = "submit" | "button" | "reset" | undefined;

interface ButtonPropTypes {
    mode?: ButtonModeE,
    intent?: ButtonIntentE,
    label?: string;
    form?: string;
    onClick?: (e?: any) => void;
    disabled?: boolean;
    loading?: boolean;
    icon?: string;
    onSubmit?: any;
    className?: string;
    style?: CSSProperties;
    type?: string;
    iconRight?: boolean;
    useGlobalLoading?: boolean;
    withoutTranslate?: boolean;
    id?: string;
    iconColor?: IconColorE;
    tabIndex?: number,
    ref?: any,
    link?: string,
    children: any;
    buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
}

/**
 * Wrapper over the default button element which adds controlled styles, icons, loading process
 * */

const Button: FC<ButtonPropTypes> = (
    {
        mode = ButtonModeE.FILLED,
        intent = ButtonIntentE.DELIVERY,
        label,
        form = '',
        children,
        onClick,
        disabled,
        loading,
        icon,
        onSubmit,
        className = '',
        style,
        type = 'submit',
        iconRight,
        useGlobalLoading = false,
        iconColor,
        id,
        tabIndex = 0,
        ref,
        link,
        withoutTranslate,
        buttonProps
    }
) => {
    const isGlobalLoading = useAppSelector(selectIsLoading);
    const direction = useAppSelector(selectDirection);
    const navigate = useNavigate();

    const handleClick = () => {
        // check if prop onClick exist and call it
        onClick && onClick();
    };

    const handleSubmit = () => {
        // check if prop onSubmit exist and call it
        onSubmit && onSubmit();
    }

    const handleLinkClick = (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        navigate(link);
    };

    // spec 48 — the glyph follows the entity colour on outlined/link buttons; a filled button's icon stays white.
    const defaultIconColor = mode === ButtonModeE.FILLED
        ? IconColorE.WHITE
        : intent === ButtonIntentE.CAMPAIGN ? IconColorE.GREEN : IconColorE.BLUE;
    const intentClass = intent === ButtonIntentE.CAMPAIGN ? styles.campaign : '';

    const ButtonIcon = () => (
        <>
            {icon && !loading && (useGlobalLoading ? !isGlobalLoading : true) && (
                <div className={styles['iconWrapper-' + direction]}>
                    <Icon
                        name={icon}
                        color={iconColor || defaultIconColor}
                        size={IconSizeE.S}
                    />
                </div>
            )}
            {(useGlobalLoading ? isGlobalLoading : loading) && (
                <div className={styles['iconWrapper-' + direction]}>
                    <Icon
                        name='loading'
                        color={iconColor || defaultIconColor}
                        size={IconSizeE.S}
                    />
                </div>
            )}
        </>
    );

    const Element = () => onSubmit ? (
        <button
            type="submit"
            className={clss([styles.submit, styles[mode], intentClass, className, direction])}
            //@ts-ignore
            form={form}
            onSubmit={handleSubmit}
            disabled={disabled || (useGlobalLoading ? isGlobalLoading : disabled)}
            //@ts-ignore
            style={style}
            id={id}
            tabIndex={tabIndex}
            ref={ref}
            {...buttonProps}
        >
            <ButtonIcon />
            {withoutTranslate ? (
                <span>{label || children}</span>
            ) : (
                <Span>{label || children}</Span>
            )}

        </button>
    ) : (
        <button
            className={clss([
                styles.button,
                styles[mode],
                intentClass,
                className,
                iconRight ? styles.iconRight : '',
                direction,
            ])}
            //@ts-ignore
            form={form}
            onClick={handleClick}
            disabled={disabled || (useGlobalLoading ? isGlobalLoading : disabled)}
            type={type as ButtonTypeT}
            //@ts-ignore
            style={style}
            id={id}
            tabIndex={tabIndex}
            ref={ref}
            {...buttonProps}
        >
            <ButtonIcon />
            {withoutTranslate ? (
                <span>{label || children}</span>
            ) : (
                <Span>{label || children}</Span>
            )}
        </button>
    );

    if (link) {
        return (
            <a href={link} onClick={handleLinkClick} className={styles.anchorReset}>
                <Element />
            </a>
        )
    } else {
        return <Element />
    }
};

export default Button;